/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import { Grid, Typography, ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AppContainer from 'components/AppContainer';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _uniq from 'lodash/uniq';
import _flattenDeep from 'lodash/flattenDeep';
import { useQuery } from 'react-query';
import { showOnboardingQuestions } from 'containers/Main/actions';
import { createStructuredSelector } from 'reselect';
import MixPanel from 'utils/mixpanelService';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import TabbedLists from 'components/SurveyResult/components/Lists/TabbedLists';
import getTopicListResources from 'containers/TopicCenter/services/getTopicListResources';
import { getListsWithResources } from 'containers/TopicCenter/utils';
import { filterHandpickedResourceByCutoff } from 'containers/Resources/utils';
import { getIsAutomatic } from 'containers/EditorList/utils';
import RightArrowIcon from './arrow.svg';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    margin: '26px 0 26px 0',
  },
  titleWrapper: {
    display: 'flex',
    marginBottom: 28,
    flex: 1,
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 26,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  resourcesTitle: {
    color: '#4B4B4B',
    flexBasis: '100%',
    '&::first-letter': {
      textTransform: 'uppercase',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 24,
    },
  },
  topicItems: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  topicItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  image: {
    width: 59,
    heigh: 59,
    borderRadius: '50%',
  },
  selectedTopic: {
    border: '2px solid #EE9F00',
    padding: 2,
  },
  imageWrapper: {
    position: 'relative',
    background: 'white',
  },
  checkIcon: {
    position: 'absolute',
    bottom: 0,
    right: 0,
  },
  title: {
    fontSize: 12,
    marginTop: 10,
    lineHeight: '17px',
  },
  selectedTitle: {
    color: '#EE9F00',
  },
  shortTitle: {
    fontSize: 14,
    fontFamily: 'MadaBold',
    lineHeight: '18.6px',
  },
  listsContainer: {
    marginBottom: 26,
  },
  linkBtn: {
    fontSize: 14,
    color: theme.palette.primary.main,
    fontFamily: 'MadaRegular',
    textTransform: 'uppercase',
    '& img': {
      marginLeft: 5,
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: 8,
      marginLeft: 0,
    },
  },
}));

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
});

const Resources = ({
  title,
  approachTitle,
  topicsQuery,
  listResources,
  listInfo,
  buttonLabel,
  grayscale,
}) => {
  const classes = useStyles();
  const algoliaLocale = useAlgoliaLocale();
  const dispatch = useDispatch();
  const { clientDetails } = useSelector(stateSelector);
  const [resourceItemSiteCopy] = useSiteCopySelector(['resource-item-page']);

  const parentListTopicTitle = _get(listInfo, 'fields.whatHelpsTopics', []).map(
    el => _get(el, 'fields.title'),
  );
  const { data: topics = [], status: topicsStatus } = topicsQuery;
  const listResourcesData = _get(listResources, 'fields.resources', []);
  const listResourcesPerTopicRaw = [];
  for (const topic of topics) {
    const topicId = _get(topic, 'sys.id');
    const matchedListResources = !_isEmpty(listResourcesData)
      ? listResourcesData.find(data => {
          const whatHelpTopics = _get(data, 'fields.whatHelpsTopics');
          const topicInWhatHelps = whatHelpTopics.find(
            el => _get(el, 'sys.id') === topicId,
          );
          return topicInWhatHelps;
        })
      : null;
    if (matchedListResources) {
      listResourcesPerTopicRaw.push(matchedListResources);
    }
  }

  const clientShortName = _get(clientDetails, 'shortName');
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const clientExcludedResourceTypes =
    _get(clientDetails, 'excludeResourceTypes') || [];
  const clientListResourcesWeights = _get(
    clientDetails,
    'metadata.listResourcesWeights',
  );
  const prioritizeClientResources = _get(
    clientDetails,
    'metadata.prioritizeClientResources',
    false,
  );
  const averageRatingCutoff = _get(clientDetails, 'averageRatingCutoff');
  const expertRatingCutoff = _get(clientDetails, 'expertRatingCutoff');
  const userRatingCutoff = _get(clientDetails, 'userRatingCutoff');
  const resourcesWeightings = _get(
    clientDetails,
    'metadata.algoliaResourcesWeight',
  );

  const listResourcesPerTopic = listResourcesPerTopicRaw.map(item => {
    const fields = _get(item, 'fields', {});
    const resources = _get(fields, 'resources', []);
    const filteredResources = resources.filter(resource =>
      filterHandpickedResourceByCutoff({
        resource,
        averageRatingCutoff,
        userRatingCutoff,
        expertRatingCutoff,
      }),
    );

    return {
      ...item,
      fields: { ...fields, resources: filteredResources },
    };
  });

  const slimItems = listResourcesPerTopic.map(item => {
    const types = _get(item, 'fields.resourceTypes', []);
    const shouldApplyLengthFilter =
      _isEmpty(types) ||
      types.includes('Videos') ||
      types.includes('Podcasts') ||
      types.includes('Assessments');
    return {
      title: _get(item, 'fields.title'),
      isAutomatic: getIsAutomatic(item),
      handpicked: _get(item, 'fields.resources', [])
        .map(resource => _get(resource, 'fields.slug'))
        .filter(resource => !!resource),
      context: _get(item, 'fields.context', ''),
      types: types.map(type =>
        _get(resourceItemSiteCopy, [
          'pageCopy',
          'algoliaResourceTypeMapping',
          type,
        ]),
      ),
      topics: [
        ...parentListTopicTitle,
        ..._get(
          item,
          'fields.whatHelpsTopics',
          _get(item, 'fields.topics', []),
        ).map(listTopic => _get(listTopic, 'fields.title')),
      ],
      themesTopics: _uniq(
        _flattenDeep(
          _get(
            item,
            'fields.whatHelpsThemes',
            _get(item, 'fields.themes', []),
          ).map(listTheme => _get(listTheme, 'fields.topics')),
        )
          .filter(
            themeTopic =>
              _get(themeTopic, 'fields.reviewStatus') === 'Accepted',
          )
          .map(themeTopic => _get(themeTopic, 'fields.title')),
      ),
      audiences: _get(item, 'fields.audienceType', []).map(listAudience =>
        _get(listAudience, 'fields.name'),
      ),
      tags: _get(item, 'fields.tags', []).map(listTag =>
        _get(listTag, 'fields.title'),
      ),
      shouldApplyLengthFilter,
    };
  });

  const { data: resources } = useQuery(
    slimItems && [
      'selected-approaches-results',
      {
        slimItems,
        clientDetails,
        clientExcludedTopic,
        clientExcludedResourceTypes,
        clientListResourcesWeights,
        averageRatingCutoff,
        expertRatingCutoff,
        userRatingCutoff,
        resourcesWeightings,
        algoliaLocale,
        averageRatingLimit: 4.1,
        prioritizeClientResources,
      },
    ],
    getTopicListResources,
    { isDataEqual: () => false },
  );

  const getOnboardingPath = () => {
    const queryparams = new URLSearchParams(window.location.search);
    const onboardingPath = queryparams.get('onboarding_path');
    return onboardingPath || 'onboarding';
  };

  const dataWithResources = useMemo(
    () =>
      getListsWithResources({
        lists: listResourcesPerTopic,
        clientExcludedResourceTypes,
        resources,
        clientGroupShortName: _get(clientDetails, 'clientGroup.shortName'),
        clientShortName,
        averageRatingCutoff,
        userRatingCutoff,
        expertRatingCutoff,
        prioritizeClientResources,
      }).filter(listItem => !_isEmpty(listItem.resources)),
    [
      resources,
      listResourcesPerTopic
        .map(r => _get(r, 'sys.id'))
        .sort()
        .join(),
    ],
  );

  if (_isEmpty(listResourcesPerTopic) && topicsStatus === 'success')
    return null;

  const tabsData = dataWithResources.slice(0, topics.length);

  return (
    <Grid id="personalized-resources-landing">
      <AppContainer>
        <Grid item className={classes.titleWrapper}>
          <Typography variant="h4" className={classes.resourcesTitle}>
            {approachTitle || title}
          </Typography>
          <ButtonBase
            className={classes.linkBtn}
            disableRipple
            onClick={() => {
              const onboardingPath = getOnboardingPath();
              MixPanel.track('Onboarding - Change Topic - Clicked', {
                path: onboardingPath,
              });
              dispatch(showOnboardingQuestions(true));
            }}
          >
            {buttonLabel}
            <img src={RightArrowIcon} alt="Arrow" />
          </ButtonBase>
        </Grid>

        <Grid
          item
          xs={12}
          className={(classes.assessmentPadding, classes.listsContainer)}
        >
          <Grid container spacing={2} className={classes.container}>
            <TabbedLists
              showTabs={tabsData.length > 1}
              data={tabsData}
              isFullWidth
              grayscale={grayscale}
              useNewCarousel
              trackingPath="Onboarding"
              clientExclusiveAsResource={prioritizeClientResources}
            />
          </Grid>
        </Grid>
      </AppContainer>
    </Grid>
  );
};

export default Resources;
